// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Editor allowing only for updating the length of a given deck edge.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { cloneTemplate, fadeElementIn, fadeElementOut, getDeckImageUrl } from "../../../utils/index.js"
import { tweenDeckEditor } from "../../../layouts/deck-editor/tween.js"

// @@imports-package
import { LoadingContainer, MeasurementInput, SteppedContainer } from "../../index.js"

// @@imports-module
import { Editor } from "./Editor.js"

// @@imports-types
import { State } from "../../../types/index.js"
import { EdgeTypeEditor } from "./EdgeTypeEditor.js"
import { App } from "../../../app/index.js"

// @@body
class EdgeEditor extends Editor {
    #typeListener = (/** @type {Event} */ event) => {
        const value = /** @type {ListSelect<EdgeType>} */ (event.target).value
        this.deck.setEdgeType(this.edge, value)
    }

    constructor() {
        super()
    }

    connectedCallback() {
        super.connectedCallback()

        const topNav = cloneTemplate("edge-editor-top-nav")
        const lengthInput = cloneTemplate("edge-editor-length-input")
        const typeSelect = cloneTemplate("edge-editor-type-select")
        if (App.state.deck.type === "shed") { typeSelect.classList.add("closed") }
        const parapetInput = cloneTemplate("edge-editor-parapet-input")
        const bottomNav = cloneTemplate("edge-editor-bottom-nav")
        this.append(topNav, lengthInput, typeSelect, parapetInput, bottomNav)

        this.updateThumbnail()
        this.updateBottomNav()

        this.#lengthInput.value = this.edge.magnitude
        this.#lengthInput.updateSelectedUnitInputs()

        this.#lengthInput.addEventListener("change", () => {
            const length = this.edge.magnitude
            const deltaLength = this.#lengthInput.value - length
            if (deltaLength === 0) { return }

            this.tweening = true
            this.renderer.tween(progress => {
                const resizedLength = length + deltaLength * progress
                this.deck.resizeEdge(this.edge, Math.round(resizedLength))

                if (progress === 1) {
                    this.tweening = false
                    const event = new Event("tween-end")
                    setTimeout(() => this.dispatchEvent(event), 100)
                }
            })
        })

        if (this.deck.isSetEdgeType(this.edge)) {
            const edgeType = this.deck.getEdgeType(this.edge)
            this.#typeSelector.value = edgeType
        }
        this.#addTypeListener()

        const nextEdgeButton = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.next-edge"))
        nextEdgeButton.addEventListener("click", () => {
            const edgeNode = this.deck.edges.nodeWithValue(this.edge)

            const resizeListener = () => {
                this.removeEventListener("tween-end", resizeListener)
                this.edge = /** @type {any} */ (edgeNode?.next?.value)
            }

            if (this.tweening) {
                this.addEventListener("tween-end", resizeListener)
                this.#lengthInput.blur()
            }
            else { this.edge = /** @type {any} */ (edgeNode?.next?.value) }
        })

        // Re implement previous edge button when enabling advanced mode
        // const previousEdgeButton = /** @type {HTMLButtonElement} */ (this
        //     .querySelector("button.previous-edge"))
        // previousEdgeButton.addEventListener("click", () => {
        //     const edgeNode = /** @type {DoubleLinkedListNode<Edge>} */ (this
        //         .state
        //         .deckEditor
        //         .deck
        //         ?.edges
        //         .nodeWithValue(this.#edge))

        //     const resizeListener = () => {
        //         this.removeEventListener("tween-end", resizeListener)
        //         this.edge = edgeNode.previous?.value
        //     }

        //     if (this.tweening) {
        //         this.addEventListener("tween-end", resizeListener)
        //         this.#lengthInput.blur()
        //     }
        //     else { this.edge = edgeNode.previous?.value }
        // })

        this.querySelector("button.close-editor")
            ?.addEventListener("click", () => { this.remove() })
            // ?.addEventListener("click", () => { App.container.unloadPopover() })

        this.querySelector("#editor-close")
            ?.addEventListener("click", async () => { this.remove() })
            // ?.addEventListener("click", () => { App.container.unloadPopover() })

        this.querySelector("#edge-split")
            ?.addEventListener("click", () => {
                this.deck.splitEdge(this.edge)
                this.renderer.render({ reset: true })
                this.classList.add("inactive")
                document.querySelector("#epdm-calculator > nav")?.classList.remove("inactive")
            })
    }

    updateThumbnail() {
        const img = /** @type {HTMLImageElement} */ (this
            .querySelector("nav.edge-toolbar figure img"))
        const caption = /** @type {HTMLImageElement} */ (this
            .querySelector("nav.edge-toolbar figure span.edge-selected"))
        const edgeIndex = this.deck.edges
            .indexOfValue(this.edge)

        getDeckImageUrl(this.deck, {
            rotation: 0,
            skew: 0,
            shadowWidth: 0,
            selectedEdge: this.edge,
            forceRender: true
        })
            .then(src => {
                img.src = src
                caption.innerText = edgeIndex + 1
            })
    }

    async updateBottomNav() {
        const { dimensions, types } = this.deck.isDefined

        const nav = /** @type {HTMLElement} */ (this
            .querySelector("nav.edge-select"))
        const previousEdge = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.previous-edge"))
        const nextEdge = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.next-edge"))
        const closeEditor = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.close-editor"))

        if (nav.classList.contains("closed")) {
            if (dimensions && types) {
                previousEdge.classList.add("closed")
                nextEdge.classList.add("closed")
                closeEditor.classList.remove("closed")
            }
            else {
                previousEdge.classList.remove("closed")
                nextEdge.classList.remove("closed")
                closeEditor.classList.add("closed")
            }
            fadeElementIn(nav)
        }
        else if (dimensions && types) {
            await Promise.all([
                fadeElementOut(previousEdge),
                fadeElementOut(nextEdge)
            ])
            fadeElementIn(closeEditor)
        }
        else {
            await fadeElementOut(closeEditor)
            fadeElementIn(previousEdge)
            fadeElementIn(nextEdge)
        }
    }

    #addTypeListener() {
        this.#typeSelector
            .addEventListener("change", this.#typeListener)
    }

    #removeTypeListener() {
        this.#typeSelector
            .removeEventListener("change", this.#typeListener)
    }

    set edge(edge) {
        const setEdge = () => {
            this.renderer.selectedEdge = edge

            this.#removeTypeListener()
            if (this.deck.isSetEdgeType(edge)) {
                this.#typeSelector.value = this.deck.getEdgeType(edge)
            }
            else {
                this.#typeSelector.value = ""
            }
            this.#addTypeListener()

            this.#lengthInput.value = edge.magnitude
            this.#lengthInput.updateSelectedUnitInputs()

            this.updateThumbnail()
            this.updateBottomNav()
        }

        const resizeListener = () => {
            this.removeEventListener("tween-end", resizeListener)
            setEdge()
        }

        if (this.tweening) {
            this.addEventListener("tween-end", resizeListener)
            this.#lengthInput.blur()
        }
        else { setEdge() }
    }

    get edge() {
        return /** @type {Edge} */ (this.renderer.selectedEdge)
    }

    get length() {
        return this.edge.magnitude
    }

    get type() {
        return this.deck.isSetEdgeType(this.edge)
            ? this.deck.getEdgeType(this.edge)
            : null
    }

    get #typeSelector() {
        return /** @type {ListSelect} */ (this.querySelector("list-select"))
    }

    get #lengthInput() {
        return /** @type {MeasurementInput} */ (this
            .querySelector("measurement-input"))
    }

    static get customName() {
        return "edge-editor"
    }
}

// @@exports
export { EdgeEditor }
